import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UtensilsCrossed, ShoppingBag, Leaf, ChefHat, Truck, ShieldCheck } from 'lucide-react';
import MenuHighlights from '../components/MenuHighlights';
import WhyChooseUs from '../components/WhyChooseUs';

const TRUST_POINTS = [
  { icon: ShieldCheck, title: '100% Halal', desc: 'We only serve 100% halal food.' },
  { icon: Leaf, title: 'Fresh Ingredients', desc: 'We use the freshest ingredients for the best quality.' },
  { icon: ChefHat, title: 'Authentic Flavor', desc: 'Authentic recipes with unmatched taste.' },
  { icon: Truck, title: 'Fast Service', desc: 'Quick preparation and friendly service.' },
];

export const HERO_IMAGE =
  'https://storage.googleapis.com/happyspace-generated-images/generated/2026-08-27/94a27a6d9508.png';

export default function Home() {
  useEffect(() => {
    document.title = 'Yalla Eat - 100% Halal Shawarma, Gyros & Wings | Fresh to Order in Plano, TX';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Authentic 100% halal shawarma, gyro platters, burgers, and wings in Plano, TX. Fresh ingredients, halal certified, ready in ~20 minutes. Order online now.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://yallaeathalalfood.com/');
  }, []);

  return (
    <>
      {/* ---------- Hero ---------- */}
      <section id="home" className="relative overflow-hidden" style={{ background: 'var(--color-ink)' }}>
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Chicken shawarma roll with fries and a drink"
            className="hero-kenburns w-full h-full object-cover"
          />
          {/* Desktop: horizontal scrim, text sits on the dark left half. */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />
          {/* Mobile: the copy spans the full width, so the horizontal scrim alone
              leaves it over the bright side of the photo — add a flat scrim below md. */}
          <div className="absolute inset-0 bg-black/55 md:hidden" />
        </div>

        <div className="relative max-w-screen-xl mx-auto px-5 md:px-8 pt-14 md:pt-20 lg:pt-24 pb-20 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-xl"
          >
            <h1 className="display text-[56px] sm:text-7xl lg:text-[86px] text-white">
              100%
              <br />
              <span style={{ color: 'var(--color-green)' }}>Halal</span>
              <br />
              Food
            </h1>

            <p className="mt-7 display text-2xl md:text-3xl text-white rule-gold">
              Delicious. Fresh. <span style={{ color: 'var(--color-green)' }}>Halal.</span>
            </p>

            <p className="mt-6 text-[15px] md:text-base text-white/80 leading-relaxed max-w-md">
              Experience authentic 100% halal shawarma, gyro platters, burgers &amp; wings at Yalla Eat in Plano, TX. Fresh ingredients. Halal certified. Ready in ~20 minutes.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/menu" className="btn-green">
                <UtensilsCrossed size={16} /> View Menu
              </Link>
              <Link to="/menu" className="btn-yellow">
                <ShoppingBag size={16} /> Order Now
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 pointer-events-none" aria-hidden="true">
          <svg viewBox="0 0 1440 130" preserveAspectRatio="none" className="w-full h-[44px] md:h-[68px] block">
            <path d="M0,96 C300,26 560,120 820,74 C1060,32 1240,84 1440,52 L1440,130 L0,130 Z" fill="var(--color-ink)" opacity="0.9" />
            <path d="M0,110 C300,44 560,132 820,88 C1060,48 1240,96 1440,68 L1440,130 L0,130 Z" fill="var(--color-yellow)" />
            <path d="M0,124 C300,64 560,142 820,104 C1060,68 1240,110 1440,88 L1440,130 L0,130 Z" fill="var(--color-cream)" />
          </svg>
        </div>
      </section>

      {/* ---------- Trust strip ---------- */}
      <section style={{ background: 'var(--color-cream)' }}>
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 pt-8 md:pt-10 pb-4 md:pb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_POINTS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className={`flex items-start gap-4 px-2 lg:px-7 py-4 lg:py-0 ${i > 0 ? 'lg:border-l' : ''}`}
              style={{ borderColor: 'rgba(18,18,18,0.12)' }}
            >
              <span
                className="shrink-0 w-14 h-14 rounded-full border-2 flex items-center justify-center"
                style={{ borderColor: 'var(--color-yellow)', background: '#fff' }}
              >
                <p.icon size={24} style={{ color: 'var(--color-ink)' }} />
              </span>
              <div>
                <h3
                  className="text-[13px] font-extrabold uppercase tracking-wide"
                  style={{ color: 'var(--color-ink)' }}
                >
                  {p.title}
                </h3>
                <p className="mt-1.5 text-[13px] text-ink-2 leading-snug max-w-[170px]">{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <MenuHighlights />
      <WhyChooseUs />

      {/* JSON-LD FAQ for common halal food questions */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `{\n        "@context": "https://schema.org",\n        "@type": "FAQPage",\n        "mainEntity": [\n          {\n            "@type": "Question",\n            "@id": "https://yallaeathalalfood.com/#what-is-halal",\n            "name": "What does halal mean?",\n            "acceptedAnswer": {\n              "@type": "Answer",\n              "text": "Halal means permissible under Islamic dietary law. Halal meat is slaughtered in a specific way, animals are treated humanely, and no pork or haram ingredients are used. At Yalla Eat, every dish is 100% halal certified."\n            }\n          },\n          {\n            "@type": "Question",\n            "@id": "https://yallaeathalalfood.com/#halal-food-near-me",\n            "name": "Where can I find halal food near me?",\n            "acceptedAnswer": {\n              "@type": "Answer",\n              "text": "Search 'halal food near me' or 'halal restaurant near me' on Google Maps to find certified halal restaurants in your area. In Plano, TX, Yalla Eat is a dedicated 100% halal kitchen serving fresh shawarma, gyro platters, burgers, and wings. Check for halal badges and certifications on listings."\n            }\n          },\n          {\n            "@type": "Question",\n            "@id": "https://yallaeathalalfood.com/#what-is-shawarma",\n            "name": "What is shawarma?",\n            "acceptedAnswer": {\n              "@type": "Answer",\n              "text": "Shawarma is a Middle Eastern dish where seasoned meat (lamb, beef, or chicken) is marinated, cooked on a vertical spit, then carved fresh to order. It's typically served wrapped in pita bread with fresh vegetables and sauce. At Yalla Eat, our shawarma is made daily with authentic spices and halal-certified meat."\n            }\n          },\n          {\n            "@type": "Question",\n            "@id": "https://yallaeathalalfood.com/#difference-gyro-shawarma",\n            "name": "What's the difference between a gyro and shawarma?",\n            "acceptedAnswer": {\n              "@type": "Answer",\n              "text": "Shawarma is Middle Eastern (Arab), while gyro is Greek. Shawarma tends to use different spices and is often wrapped in pita, while gyro is typically served on a platter with rice. Both are carved from seasoned meat cooked on a spit. Yalla Eat serves authentic halal shawarma and NYC-style gyro platters."\n            }\n          },\n          {\n            "@type": "Question",\n            "@id": "https://yallaeathalalfood.com/#how-long-pickup",\n            "name": "How long does halal food take to prepare?",\n            "acceptedAnswer": {\n              "@type": "Answer",\n              "text": "Fresh halal food made to order typically takes 15–25 minutes. At Yalla Eat, we aim for ~20 minutes from order to pickup. This ensures your shawarma, gyro, or wings are prepared fresh, not sitting under heat lamps. Order online or call ahead to maximize speed."\n            }\n          },\n          {\n            "@type": "Question",\n            "@id": "https://yallaeathalalfood.com/#halal-falafel-hummus",\n            "name": "Do you serve vegetarian halal options like falafel and hummus?",\n            "acceptedAnswer": {\n              "@type": "Answer",\n              "text": "Yes, Yalla Eat offers halal-certified falafel, hummus, and other Mediterranean vegetarian sides alongside our meat dishes. All ingredients are halal certified, making them perfect for vegetarians and those wanting lighter options."\n            }\n          }\n        ]\n      }` }} />
    </>
  );
}
